// v3.99c.67 — Reusable Tool Gateway. Shows a value-first signup card
// over CannaForge's interactive genetics tools when the user is logged
// out. The page header / SEO copy / sample-cards stay public above the
// gateway; full interactivity is unlocked behind a free account.
//
// Mirrors the article gateway philosophy (CannaForge v3.99c.66): preserve
// SEO content in the DOM, gate only the deeper interactive UX.
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";

export default function ToolGateway({
  title = "Unlock the full CannaForge genetics tools",
  description = "Create a free Collector/Grower account to explore the full Genetics Map, Trait Matrix, family hubs, strain relationships, and breeder-connected genetics.",
  eyebrow = "Member tool",
  testid = "tool-gateway",
  children = null,
}) {
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <aside
      data-testid={testid}
      className="my-8 border border-[var(--gold)]/40 bg-[var(--surface)] p-6 sm:p-8 relative overflow-hidden"
      style={{ borderRadius: 2 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Lock className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} />
        <span
          className="text-[10px] font-mono uppercase tracking-[0.22em]"
          style={{ color: "var(--gold)" }}
        >
          {eyebrow}
        </span>
      </div>
      <h3
        className="font-display text-2xl md:text-3xl tracking-tight mb-3"
        style={{ color: "var(--text)" }}
      >
        {title}
      </h3>
      <p
        className="text-sm md:text-base leading-relaxed mb-6 max-w-2xl"
        style={{ color: "var(--text-2)" }}
      >
        {description}
      </p>
      <div className="flex flex-wrap gap-3 mb-4">
        <Link
          to={`/register?next=${next}`}
          data-testid={`${testid}-register`}
          className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] text-sm font-semibold transition-colors"
          style={{
            background: "var(--gold)",
            color: "var(--bg)",
            borderRadius: 2,
          }}
        >
          Create free Collector/Grower account
        </Link>
        <Link
          to={`/for-breeders`}
          data-testid={`${testid}-apply-breeder`}
          className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] text-sm border transition-colors hover:bg-[var(--bg)]"
          style={{
            borderColor: "var(--border-strong)",
            color: "var(--text)",
            borderRadius: 2,
          }}
        >
          Apply as Breeder / Vendor
        </Link>
        <Link
          to={`/login?next=${next}`}
          data-testid={`${testid}-login`}
          className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] text-sm transition-colors hover:underline"
          style={{ color: "var(--text-2)" }}
        >
          Sign in
        </Link>
      </div>
      <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
        Collector and grower accounts are free to start. Breeder/vendor
        marketplace tools may require approval.
      </p>
      {/* v3.99c.72 — Subtle Forge Feed link to remind users the feed is
          part of the same free-account bundle. Suppressed when:
          (a) the caller supplied custom `children` (page is already
              showing related UX), or
          (b) the current route is /forge-feed itself (avoid circular
              self-link to the page the user is already on). */}
      {!children && !location.pathname.startsWith("/forge-feed") && (
        <p className="mt-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
          You&rsquo;ll also get full <Link to="/forge-feed"
            className="underline hover:text-[var(--gold)]"
            data-testid={`${testid}-forge-feed-link`}
            style={{ color: "var(--text-2)" }}>Forge Feed</Link>{" "}
          access — vote on posts, follow breeder activity, and catch new drops
          as they happen.
        </p>
      )}
      {children}
    </aside>
  );
}
